import { getUnexpiredToken } from "@/utils/utils";
import { create } from "zustand";

interface CanvasLaunchData {
  projectLaunchStartTime: number;
  projectLaunchTraceId: string;
  navTreeReceived: boolean; // Whether we've received any nav tree (fresh or cached)
  launchRequestCompleteTs: number | null; // When we've fully sent all the requests for the container/loadcodebase/startdevserver to the backend
  publishLoadStatsTimeoutId: NodeJS.Timeout | null; // Timeout for publishing stats
  publishLoadStatsScheduledTime: number | null; // When we expect to publish stats
  loadStatsPublished: boolean; // Whether load stats have been published
}

interface TelemetryState {
  canvasLaunches: Record<string, CanvasLaunchData>;
  startProjectLaunch: (params: {
    canvasId: string;
    traceId: string;
  }) => Promise<void>;
  markProjectLaunchRequestComplete: (params: { canvasId: string }) => void;
  markNavTreeReceived: (params: { canvasId: string; cached: boolean }) => void;
  markScriptReady: (params: { canvasId: string; iframeId: string }) => void;
}

export const useTelemetryStore = create<TelemetryState>((set, get) => ({
  canvasLaunches: {},

  startProjectLaunch: async ({ canvasId, traceId }) => {
    set((state) => ({
      canvasLaunches: {
        ...state.canvasLaunches,
        [canvasId]: {
          projectLaunchStartTime: Date.now(),
          projectLaunchTraceId: traceId,
          navTreeReceived: false,
          launchRequestCompleteTs: null,
          publishLoadStatsTimeoutId: null,
          publishLoadStatsScheduledTime: null,
          loadStatsPublished: false,
        },
      },
    }));

    // Send project launch started event
    await sendLoadTrackingEvent({
      canvasId,
      traceId,
      eventType: "web_project_launch",
      eventData: { startMs: Date.now() },
    });
  },

  markProjectLaunchRequestComplete: async ({ canvasId }) => {
    const { canvasLaunches } = get();
    const launchData = canvasLaunches[canvasId];
    if (!launchData) {
      return;
    }

    set((state) => ({
      canvasLaunches: {
        ...state.canvasLaunches,
        [canvasId]: {
          ...launchData,
          launchRequestCompleteTs: Date.now(),
        },
      },
    }));

    // Send project launch request complete
    await sendLoadTrackingEvent({
      canvasId,
      traceId: launchData.projectLaunchTraceId,
      eventType: "web_project_launch",
      eventData: { endMs: Date.now() },
    });

    // Check if we should start publishing timer (both request complete and navtree received)
    const updatedLaunchData = get().canvasLaunches[canvasId];
    const loadStatsDelay = updatedLaunchData?.navTreeReceived
      ? 10 * 1000
      : 5 * 60 * 1000;
    maybeSchedulePublishLoadStats(canvasId, loadStatsDelay);
  },

  markNavTreeReceived: async ({ canvasId, cached }) => {
    const { canvasLaunches } = get();
    const launchData = canvasLaunches[canvasId];

    // Only track if we're in a project launch flow for this canvas and haven't
    // received a nav tree event yet
    if (!launchData || launchData.navTreeReceived) {
      return;
    }

    // Mark that we've received a nav tree
    set((state) => ({
      canvasLaunches: {
        ...state.canvasLaunches,
        [canvasId]: {
          ...launchData,
          navTreeReceived: true,
        },
      },
    }));

    // Send nav tree loaded event
    await sendLoadTrackingEvent({
      canvasId,
      traceId: launchData.projectLaunchTraceId,
      eventType: "web_nav_tree_loaded",
      eventData: { endMs: Date.now(), cached },
    });

    // Check if we should start publishing timer (both request complete and navtree received)
    const updatedLaunchData = get().canvasLaunches[canvasId];
    const loadStatsDelay = updatedLaunchData?.launchRequestCompleteTs
      ? 10 * 1000
      : 5 * 60 * 1000;
    maybeSchedulePublishLoadStats(canvasId, loadStatsDelay);
  },

  markScriptReady: async ({ canvasId, iframeId }) => {
    const { canvasLaunches } = get();
    const launchData = canvasLaunches[canvasId];

    // Only track if we're in a project launch flow
    if (!launchData) {
      return;
    }

    // Send script ready event to backend
    await sendLoadTrackingEvent({
      canvasId,
      traceId: launchData.projectLaunchTraceId,
      eventType: "web_script_ready",
      eventData: { iframeId, endMs: Date.now() },
    });
  },
}));

// Helper function to schedule publish of load stats
// If called multiple times, replaces with sooner timeout if the new scheduled time is earlier
function maybeSchedulePublishLoadStats(
  canvasId: string,
  delayMs: number,
): void {
  const { canvasLaunches } = useTelemetryStore.getState();
  const launchData = canvasLaunches[canvasId];

  if (!launchData || launchData.loadStatsPublished) {
    return;
  }

  const now = Date.now();
  const newScheduledTime = now + delayMs;

  // If we already have a scheduled time and the new one is not sooner, don't reschedule
  if (
    launchData.publishLoadStatsScheduledTime &&
    newScheduledTime >= launchData.publishLoadStatsScheduledTime
  ) {
    return;
  }

  // Clear any existing timeout
  if (launchData.publishLoadStatsTimeoutId) {
    clearTimeout(launchData.publishLoadStatsTimeoutId);
  }

  // Set up timeout to publish load stats
  const timeoutId = setTimeout(async () => {
    const currentLaunchData =
      useTelemetryStore.getState().canvasLaunches[canvasId];
    if (currentLaunchData && !currentLaunchData.loadStatsPublished) {
      await publishLoadStats({
        canvasId,
        traceId: currentLaunchData.projectLaunchTraceId,
      });

      // Mark as published
      useTelemetryStore.setState((state) => ({
        canvasLaunches: {
          ...state.canvasLaunches,
          [canvasId]: {
            ...state.canvasLaunches[canvasId],
            loadStatsPublished: true,
            publishLoadStatsTimeoutId: null,
            publishLoadStatsScheduledTime: null,
          },
        },
      }));
    }
  }, delayMs);

  useTelemetryStore.setState((state) => ({
    canvasLaunches: {
      ...state.canvasLaunches,
      [canvasId]: {
        ...launchData,
        publishLoadStatsTimeoutId: timeoutId,
        publishLoadStatsScheduledTime: newScheduledTime,
      },
    },
  }));
}

// Generic helper to send load tracking events to the backend
export async function sendLoadTrackingEvent(params: {
  canvasId: string;
  traceId: string;
  eventType: `web_${string}`;
  eventData: Record<string, any>;
}): Promise<void> {
  const { canvasId, traceId, eventType, eventData } = params;
  await sendAuthenticatedRequest({
    url: `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/canvases/${canvasId}/load-tracker/trackLoadEvent`,
    traceId,
    body: {
      eventType,
      ...eventData,
    },
  });
}

// Generic helper to send authenticated backend requests with trace ID
async function sendAuthenticatedRequest(params: {
  url: string;
  traceId: string;
  body?: Record<string, any>;
}): Promise<void> {
  const { url, traceId, body } = params;

  try {
    const token = await (window as any).Clerk.session?.getToken();
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-trace-id": traceId,
      },
      ...(body && { body: JSON.stringify(body) }),
    });
  } catch (error) {
    console.debug("Failed to send telemetry request", {
      url,
      body,
      error,
    });
  }
}

// Triggers a publish of aggregated load stats for a given trace all in a single
// event for easy consumption in Posthog
async function publishLoadStats(params: {
  canvasId: string;
  traceId: string;
}): Promise<void> {
  const { canvasId, traceId } = params;
  await sendAuthenticatedRequest({
    url: `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/canvases/${canvasId}/load-tracker/publishLoadStats`,
    traceId,
  });
}
